import { useState, useEffect, useRef, useCallback } from "react";
import {
  LiveAvatarSession,
  AgentEventsEnum,
  SessionState,
} from "@heygen/liveavatar-web-sdk";
import Cookies from "js-cookie";

/**
 * Custom hook to manage LiveAvatar session with SDK
 * This replaces the old manual LiveKit connection approach
 */
export const useLiveAvatarSession = () => {
  const sessionRef = useRef(null);
  const [sessionState, setSessionState] = useState(SessionState.INACTIVE);
  const [isStreamReady, setIsStreamReady] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [micUnmute, setMicUnmute] = useState(true); // Start as unmuted (matches SDK default)
  const [isUserTalking, setIsUserTalking] = useState(false);
  const [isAvatarTalking, setIsAvatarTalking] = useState(false);
  const [voiceChatActive, setVoiceChatActive] = useState(false); // Track if voice chat is ACTIVE
  const [questionLimitReached, setQuestionLimitReached] = useState(false);
  const updateQuestionCount = useCallback(async () => {
    try {
      const token = Cookies.get("token");
      if (!token) {
        console.warn("No auth token found - skipping question count update");
        return;
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/user/updateQuestionCount`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            token: token,
          },
          credentials: "include",
        },
      );

      const data = await response.json();

      if (
        response.status === 400 &&
        data.message &&
        data.message.includes("reached limits")
      ) {
        console.warn("Question limit reached:", data.message);
        setQuestionLimitReached(true);
        return;
      }

      if (!response.ok || !data.success) {
        console.error("Failed to update question count:", data.message);
      } else {
      }
    } catch (error) {
      console.error("Error updating question count:", error);
    }
  }, []);

  /**
   * Initialize the LiveAvatar session
   * @param {string} sessionToken - Session token from backend
   */
  const initSession = useCallback(
    async (sessionToken) => {
      try {
        // Create new LiveAvatar session with SDK
        const session = new LiveAvatarSession(sessionToken, {
          voiceChat: true,
        });

        sessionRef.current = session;

        // Listen to session state changes
        session.on("session.state_changed", (state) => {
          setSessionState(state);
        });

        // Listen to voice chat state changes (like HeyGen demo)
        if (session.voiceChat) {
          session.voiceChat.on("state_changed", (state) => {
            const isActive = state === "ACTIVE";
            setVoiceChatActive(isActive);

            // If voice chat becomes active and we haven't set mic state yet,
            // check if it's muted to sync UI
            if (isActive) {
              // Voice chat is ACTIVE - check current mute state
              // Note: We'll rely on MUTED/UNMUTED events for accurate state
            }
          });

          // Listen to mute/unmute events (like HeyGen demo)
          session.voiceChat.on("muted", () => {
            setMicUnmute(false);
          });

          session.voiceChat.on("unmuted", () => {
            setMicUnmute(true);
          });
        }

        // Listen to stream ready event
        session.on("session.stream_ready", () => {
          setIsStreamReady(true);
        });

        // Listen to user speech events
        session.on(AgentEventsEnum.USER_SPEAK_STARTED, () => {
          setIsUserTalking(true);
        });

        session.on(AgentEventsEnum.USER_SPEAK_ENDED, () => {
          setIsUserTalking(false);
        });

        // Listen to avatar speech events
        session.on(AgentEventsEnum.AVATAR_SPEAK_STARTED, () => {
          setIsAvatarTalking(true);
        });

        session.on(AgentEventsEnum.AVATAR_SPEAK_ENDED, () => {
          setIsAvatarTalking(false);
        });
        session.on(AgentEventsEnum.USER_TRANSCRIPTION, (event) => {
          setConversations((prev) => [
            ...prev,
            {
              user_text: event.text,
              avatar_response: "",
            },
          ]);
          updateQuestionCount();
        });

        // Listen to avatar transcriptions (avatar speech to text)
        session.on(AgentEventsEnum.AVATAR_TRANSCRIPTION, (event) => {
          setConversations((prev) => {
            const newConv = [...prev];
            if (newConv.length > 0) {
              // Append to the last conversation's avatar response
              const lastConv = newConv[newConv.length - 1];
              newConv[newConv.length - 1] = {
                ...lastConv,
                avatar_response: (lastConv.avatar_response || "") + event.text,
              };
            } else {
              // First message is from avatar
              newConv.push({
                user_text: "",
                avatar_response: event.text,
              });
            }
            return newConv;
          });
        });
        await session.start();

        return session;
      } catch (error) {
        console.error("Error initializing LiveAvatar session:", error);
        throw error;
      }
    },
    [updateQuestionCount],
  );

  /**
   * Attach video element to the session
   * @param {HTMLVideoElement} videoElement - Video element to attach
   */
  const attachVideo = useCallback((videoElement) => {
    if (!sessionRef.current || !videoElement) {
      console.error("Cannot attach video: session or element not available");
      return;
    }

    try {
      sessionRef.current.attach(videoElement);
    } catch (error) {
      console.error("Error attaching video:", error);
    }
  }, []);

  /**
   * Send a text message to the avatar
   * @param {string} text - Message text
   */
  const sendMessage = useCallback(async (text) => {
    if (!sessionRef.current || !text.trim()) return;

    try {
      // Don't add message here - let the SDK transcription event handle it
      // to avoid duplicates. The SDK will emit USER_TRANSCRIPTION event.

      // Send message through SDK (this will trigger avatar response)
      await sessionRef.current.message(text);
    } catch (error) {
      console.error("Error sending message:", error);
    }
  }, []);

  /**
   * Toggle microphone on/off
   * Simplified: just toggle mute/unmute and update state directly
   */
  const toggleMic = useCallback(async () => {
    if (!sessionRef.current || !sessionRef.current.voiceChat) return;

    try {
      const vcState = sessionRef.current.voiceChat.state;

      // If voice chat is INACTIVE, start it first
      if (vcState === "INACTIVE") {
        await sessionRef.current.voiceChat.start();
        setMicUnmute(true); // Starts unmuted
        return;
      }

      // Voice chat is ACTIVE - toggle mute/unmute based on current state
      if (micUnmute) {
        await sessionRef.current.voiceChat.mute();
        setMicUnmute(false);
      } else {
        await sessionRef.current.voiceChat.unmute();
        setMicUnmute(true);
      }
    } catch (error) {
      console.error("Error toggling mic:", error);
    }
  }, [micUnmute]);

  /**
   * Start voice chat
   */
  const startVoiceChat = useCallback(async () => {
    if (!sessionRef.current || !sessionRef.current.voiceChat) return;

    try {
      await sessionRef.current.voiceChat.start();
    } catch (error) {
      console.error("Error starting voice chat:", error);
    }
  }, []);

  /**
   * Stop voice chat
   */
  const stopVoiceChat = useCallback(async () => {
    if (!sessionRef.current || !sessionRef.current.voiceChat) return;

    try {
      await sessionRef.current.voiceChat.stop();
      setMicUnmute(false);
    } catch (error) {
      console.error("Error stopping voice chat:", error);
    }
  }, []);

  /**
   * Interrupt the avatar (stop current speech)
   */
  const interrupt = useCallback(async () => {
    if (!sessionRef.current) return;

    try {
      await sessionRef.current.interrupt();
    } catch (error) {
      console.error("Error interrupting avatar:", error);
    }
  }, []);

  /**
   * End the session
   */
  const endSession = useCallback(async () => {
    if (!sessionRef.current) return;

    try {
      await sessionRef.current.stop();
      sessionRef.current = null;
      setSessionState(SessionState.DISCONNECTED);
      setIsStreamReady(false);
      setMicUnmute(false);
      console.log("Session ended");
    } catch (error) {
      console.error("Error ending session:", error);
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (sessionRef.current) {
        sessionRef.current.stop().catch(console.error);
      }
    };
  }, []);

  return {
    sessionRef,
    sessionState,
    isStreamReady,
    conversations,
    micUnmute,
    isUserTalking,
    isAvatarTalking,
    voiceChatActive, // Whether voice chat is in ACTIVE state
    questionLimitReached,
    initSession,
    attachVideo,
    sendMessage,
    toggleMic,
    interrupt,
    startVoiceChat,
    stopVoiceChat,
    endSession,
  };
};
